import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { quoteService } from '../services/quoteService';
import { analytics, trackDashboardUsage } from '../services/analytics';
import { quotePDFService } from '../services/quotePDFService';
import { Eye as EyeIcon, Trash as TrashIcon, Plus as PlusIcon, Settings as SettingsIcon, BarChart3 as BarChart3Icon, Copy as CopyIcon, RefreshCw as RefreshCwIcon, Download as DownloadIcon } from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ⚙️ HELPER FUNCTIONS: Status and formatting utilities
  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        // High-contrast for accepted status
        return 'bg-black text-white border-black';
      case 'pending':
        // Light gray for pending status
        return 'bg-gray-100 text-black border-gray-300';
      case 'expired':
        // Darker gray for expired status
        return 'bg-gray-400 text-white border-gray-500';
      default:
        // Default very light gray
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // 👁️ VIEW STATUS: Display quote view tracking data
  const getViewStatus = (quote) => {
    const viewCount = quote.viewCount || 0;
    const firstViewedAt = quote.firstViewedAt;
    const lastViewedAt = quote.lastViewedAt;

    if (viewCount === 0) {
      return (
        <div className="text-gray-500">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span>Not viewed</span>
          </div>
        </div>
      );
    }

    return (
      <div className="text-black">
        <div className="flex items-center space-x-2 mb-1">
          <div className="w-2 h-2 bg-black rounded-full"></div>
          <span className="font-medium">
            {viewCount} {viewCount === 1 ? 'view' : 'views'}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          First: {formatDateTime(firstViewedAt)}
        </div>
        {lastViewedAt && lastViewedAt !== firstViewedAt && (
          <div className="text-xs text-gray-500">
            Last: {formatDateTime(lastViewedAt)}
          </div>
        )}
      </div>
    );
  };

  // ⚡ PERFORMANCE OPTIMIZED: Lazy loading with minimal initial load
  const loadQuotes = useCallback(async (showRefreshIndicator = false, bypassCache = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const quoteResponse = await quoteService.getQuotes(bypassCache);
      const quotesData = quoteResponse.data || [];
      
      setQuotes(quotesData);
      
      // 🎯 SUCCESS: Show success message for manual refreshes
      if (showRefreshIndicator) {
        toast.success(`Dashboard refreshed! Found ${quotesData.length} quotes`);
      }
      
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // ⚡ OPTIMIZED: Delayed initial load for faster signin
  useEffect(() => {
    if (!user?.id) return; // Don't load if no user
    
    // 🎯 PERFORMANCE: Track page view immediately but delay data loading
    analytics.trackPageView('Dashboard');
    
    // Set user ID for analytics (only when user ID actually changes)
    analytics.setUserId(user.id);
    
    // ⚡ DELAYED LOAD: Load quotes after a brief delay to improve signin UX
    const loadTimer = setTimeout(() => {
      loadQuotes();
    }, 500); // 500ms delay allows signin to complete smoothly
    
    return () => clearTimeout(loadTimer);
  }, [user?.id]); // Remove loadQuotes dependency to prevent infinite loops

  // Focus refresh removed to prevent automatic refreshing when user returns to tab

  // 🚀 ENHANCED: Manual refresh function
  const handleManualRefresh = () => {
    loadQuotes(true, true); // Show refresh indicator AND bypass cache
  };

  // 🚀 ENHANCED: Delete with immediate UI update
  const handleDeleteQuote = async (quoteId) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      try {
        // 🎯 OPTIMISTIC UPDATE: Remove from UI immediately
        const updatedQuotes = quotes.filter(q => q.id !== quoteId);
        setQuotes(updatedQuotes);
        
        // Then delete from database
        await quoteService.deleteQuote(quoteId);
        toast.success('Quote deleted successfully');
        
        // 🔄 REFRESH: Load fresh data to ensure consistency
        setTimeout(() => loadQuotes(), 500);
      } catch (error) {
        toast.error('Failed to delete quote');
        // 🔄 REVERT: Reload data if delete failed
        loadQuotes();
      }
    }
  };

  const copyQuoteLink = (uniqueId) => {
    navigator.clipboard.writeText(`${window.location.origin}/quote/${uniqueId}`);
    toast.success('Link copied!');
  };

  const handleDownloadPDF = async (quote) => {
    try {
      console.log('📄 Starting PDF download...');
      const pdf = await quotePDFService.generateQuotePDF(quote, {
        signatureName: quote.clientSignatureName || 'Digital Signature',
        signedAt: quote.signedAt || new Date().toISOString(),
      });
      pdf.save(`${quote.owner?.businessName || 'Quote'}-${quote.uniqueId}.pdf`);
  
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error(err?.message || 'Download failed');
    }
  };

  // Note: Avoid a full-screen loader here to prevent
  // double loaders with the route-level auth loader.

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <div className="border-b border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-8">
          <div className="flex items-center justify-between">
            {/* Logo/Brand */}
            <div className="flex items-center">
              <h1 className={`font-instrument text-2xl md:text-4xl tracking-tight ${!loading ? 'reveal-header reveal-header-1' : ''}`}>Dashboard</h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className={`hidden md:flex items-center space-x-6 ${!loading ? 'reveal-header reveal-header-2' : ''}`}>
              <p className="font-jetbrains text-gray-600 text-lg">Welcome back, {user?.profile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}</p>
              <button 
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-3 py-2 border border-black rounded hover:bg-gray-100 disabled:opacity-50"
                title="Refresh dashboard data"
              >
                <RefreshCwIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="font-jetbrains text-sm">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <Link to="/profile" className="font-jetbrains text-black hover:text-gray-700">Profile</Link>
              <button onClick={() => { analytics.track('User Logged Out'); logout(); }} className="font-jetbrains text-black hover:text-gray-700">Sign out</button>
            </div>
            
            {/* Mobile Navigation - Two Buttons */}
            <div className="md:hidden flex items-center space-x-2">
              <Link to="/profile" className="font-jetbrains text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">Profile</Link>
              <button 
                onClick={() => { analytics.track('User Logged Out'); logout(); }} 
                className="font-jetbrains text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
          
          {/* Mobile Welcome Message */}
          <div className="md:hidden mt-4">
            <p className="font-jetbrains text-gray-600 text-base">Welcome back, {user?.profile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link to="/quote-builder" className={`border border-black rounded-lg p-6 hover:bg-gray-100 hover:shadow-lg group transition-all duration-300 ${!loading ? 'reveal-card reveal-1' : ''}`}>
            <div className="flex items-center"><PlusIcon className="w-5 h-5 mr-4" /><div><h3 className="font-instrument text-xl">Create Quote</h3><p className="text-sm text-gray-600">New interactive quote</p></div></div>
          </Link>
          <Link to="/products" className={`border border-black rounded-lg p-6 hover:bg-gray-100 hover:shadow-lg group transition-all duration-300 ${!loading ? 'reveal-card reveal-2' : ''}`}>
            <div className="flex items-center"><SettingsIcon className="w-5 h-5 mr-4" /><div><h3 className="font-instrument text-xl">Manage Products</h3><p className="text-sm text-gray-600">Setup add-ons</p></div></div>
          </Link>
          <Link to="/analytics" className={`border border-black rounded-lg p-6 hover:bg-gray-100 hover:shadow-lg group transition-all duration-300 ${!loading ? 'reveal-card reveal-3' : ''}`}>
            <div className="flex items-center"><BarChart3Icon className="w-5 h-5 mr-4" /><div><h3 className="font-instrument text-xl">Analytics</h3><p className="text-sm text-gray-600">View detailed insights</p></div></div>
          </Link>
        </div>
        
        {/* Quotes List */}
        <div className={`mb-8 ${!loading ? 'reveal reveal-4' : ''}`}>
          <div className="flex items-center justify-between">
            <h2 className="font-instrument text-3xl md:text-4xl tracking-tight">Recent Quotes</h2>
            {/* Manual refresh button for mobile */}
            <div className="md:hidden">
              <button 
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="mobile-action-btn bg-gray-100 hover:bg-gray-200 text-gray-700"
                title="Refresh dashboard data"
              >
                <RefreshCwIcon className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Inline loading state (no full-screen) */}
        {loading && (
          <div className="border border-black rounded-lg p-8 text-center mb-8 shimmer-overlay">
            <div className="relative z-10">
              <div className="spinner spinner-lg mx-auto mb-4" aria-hidden="true"></div>
              <p className="font-jetbrains text-gray-600">Loading your dashboard...</p>
              <p className="font-jetbrains text-xs text-gray-400 mt-2">Preparing your premium experience</p>
            </div>
          </div>
        )}

        {/* Desktop Table */}
        {!loading && (
        <div className="hidden md:block border border-black rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 reveal reveal-5">
          {/* Desktop Table Header */}
          <div className="grid grid-cols-12 gap-4 bg-gray-100 border-b border-black p-4 font-jetbrains font-medium text-sm text-black">
            <div className="col-span-3">Client</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3">View Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          
          {/* Desktop Quotes Data */}
          {quotes.length > 0 ? (
            quotes.map((quote, idx) => (
              <div key={quote.id} className={`p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-200 ${idx < 8 ? `reveal reveal-${Math.min(idx + 1, 6)}` : ''}`}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Client Info */}
                  <div className="col-span-3">
                    <h3 className="font-jetbrains font-medium text-black text-sm">{quote.clientName}</h3>
                    <p className="font-jetbrains text-xs text-gray-600 truncate">{quote.clientEmail}</p>
                  </div>
                  
                  {/* Status */}
                  <div className="col-span-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(quote.status)}`}>{quote.status}</span>
                  </div>

                  {/* View Status */}
                  <div className="col-span-3">
                    <div className="font-jetbrains text-xs">{getViewStatus(quote)}</div>
                  </div>

                  {/* Created Date */}
                  <div className="col-span-2">
                    <span className="font-jetbrains text-xs text-gray-600">{formatDate(quote.createdAt)}</span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => navigate(`/quote/${quote.uniqueId}`)} className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="View"><EyeIcon className="w-4 h-4" /></button>
                      <button onClick={() => copyQuoteLink(quote.uniqueId)} className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="Copy Link"><CopyIcon className="w-4 h-4" /></button>
                      {quote.status === 'accepted' && (
                        <button 
                          onClick={() => handleDownloadPDF(quote)} 
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded" 
                          title="Download PDF"
                        >
                          <DownloadIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handleDeleteQuote(quote.id)} className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="Delete"><TrashIcon className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16">
              <h3 className="font-instrument text-xl">No quotes yet</h3>
              <p className="text-gray-600 mt-2">Create your first quote to get started.</p>
              <Link to="/quote-builder" className="inline-block mt-4 px-6 py-2 bg-black text-white rounded hover:bg-gray-800">
                Create Your First Quote
              </Link>
            </div>
          )}
        </div>
        )}
        
        {/* Mobile Cards */}
        {!loading && (
        <div className="md:hidden space-y-4 reveal reveal-5">
          {quotes.length > 0 ? (
            quotes.map((quote, idx) => (
              <div key={quote.id} className={`mobile-card hover:shadow-md transition-all duration-300 ${idx < 8 ? `reveal-card reveal-${Math.min(idx + 1, 6)}` : ''}`}>
                <div className="mobile-card-title font-jetbrains">
                  {quote.clientName}
                </div>
                <div className="mobile-card-content">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(quote.status)}`}>
                      {quote.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-jetbrains">{quote.clientEmail}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Created:</span>
                    <span className="ml-2 font-jetbrains">{formatDate(quote.createdAt)}</span>
                  </div>
                  <div className="pt-2">
                    <span className="text-gray-600 block mb-2">View Status:</span>
                    <div className="font-jetbrains text-sm">{getViewStatus(quote)}</div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-gray-600 font-medium">Actions:</span>
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => navigate(`/quote/${quote.uniqueId}`)} 
                        className="mobile-action-btn bg-gray-100 hover:bg-gray-200 text-gray-700" 
                        title="View"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => copyQuoteLink(quote.uniqueId)} 
                        className="mobile-action-btn bg-gray-100 hover:bg-gray-200 text-gray-700" 
                        title="Copy Link"
                      >
                        <CopyIcon className="w-5 h-5" />
                      </button>
                      {quote.status === 'accepted' && (
                        <button 
                          onClick={() => handleDownloadPDF(quote)} 
                          className="mobile-action-btn bg-green-100 hover:bg-green-200 text-green-700" 
                          title="Download PDF"
                        >
                          <DownloadIcon className="w-5 h-5" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteQuote(quote.id)} 
                        className="mobile-action-btn bg-red-100 hover:bg-red-200 text-red-700" 
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16">
              <h3 className="font-instrument text-xl">No quotes yet</h3>
              <p className="text-gray-600 mt-2">Create your first quote to get started.</p>
              <Link to="/quote-builder" className="inline-block mt-4 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 min-h-[48px]">
                Create Your First Quote
              </Link>
            </div>
          )}
        </div>
        )}
        
        {/* Dashboard Status - Professional Design */}
        <div className={`mt-12 ${!loading ? 'reveal reveal-6' : ''}`}>
          <h3 className="font-instrument text-2xl tracking-tight mb-6">System Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Manual Updates Status */}
            <div className="border border-black rounded-lg p-6 premium-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <h4 className="font-jetbrains font-medium text-black">Updates</h4>
                </div>
                <span className="font-jetbrains text-xs text-black bg-gray-200 px-2 py-1 rounded border border-gray-300">
                  MANUAL
                </span>
              </div>
              <p className="font-jetbrains text-sm text-gray-600">
                Use refresh button or return to tab to update data
              </p>
            </div>

            {/* Quotes Count */}
            <div className="border border-black rounded-lg p-6 premium-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <BarChart3Icon className="w-5 h-5 text-black" />
                  <h4 className="font-jetbrains font-medium text-black">Quotes Loaded</h4>
                </div>
                <span className="font-jetbrains text-xl font-bold text-black">
                  {quotes.length}
                </span>
              </div>
              <p className="font-jetbrains text-sm text-gray-600">
                {quotes.length === 0 ? 'No quotes created yet' : 
                 quotes.length === 1 ? 'Total quote in system' : 
                 'Total quotes in system'}
              </p>
            </div>

            {/* Last Refresh */}
            <div className="border border-black rounded-lg p-6 premium-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <RefreshCwIcon className="w-5 h-5 text-black" />
                  <h4 className="font-jetbrains font-medium text-black">Last Refresh</h4>
                </div>
                <button 
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className="font-jetbrains text-xs text-black hover:text-gray-700 underline disabled:opacity-50"
                >
                  {refreshing ? 'Refreshing...' : 'Refresh Now'}
                </button>
              </div>
              <p className="font-jetbrains text-sm text-gray-600">
                {new Date().toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit', 
                  second: '2-digit' 
                })}
              </p>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
